import React from 'react';
import { alpha, keyframes } from '@mui/material';
import { AlertShell, AlertField } from './ui/AlertShell';
import { AddressPill, AlertBotTitle } from './LowGasAlert';
import { useTheme } from '@mui/material/styles';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';

const NETWORKS: Record<number, { name: string; explorer?: string }> = {
    1: { name: 'Ethereum', explorer: 'https://etherscan.io/address/' },
    10: { name: 'Optimism', explorer: 'https://optimistic.etherscan.io/address/' },
    56: { name: 'BSC', explorer: 'https://bscscan.com/address/' },
    137: { name: 'Polygon', explorer: 'https://polygonscan.com/address/' },
    8453: { name: 'Base', explorer: 'https://basescan.org/address/' },
    42161: { name: 'Arbitrum', explorer: 'https://arbiscan.io/address/' },
    43114: { name: 'Avalanche', explorer: 'https://snowtrace.io/address/' },
    30: { name: 'Rootstock', explorer: 'https://explorer.rootstock.io/address/' },
    31: { name: 'Rootstock Testnet', explorer: 'https://explorer.testnet.rootstock.io/address/' },
};

// Helper to convert chainId to network name
const getNetworkName = (chainId?: number): string =>
    chainId ? NETWORKS[chainId]?.name || `Chain ${chainId}` : '';

// Helper to format number with ceiling rounding
const formatAmountWithCeiling = (amount: number | undefined, decimals: number = 10): string => {
    if (amount === undefined) return '';
    const multiplier = Math.pow(10, decimals);
    const ceiledAmount = Math.ceil(amount * multiplier) / multiplier;
    return ceiledAmount.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

interface WaitingFundsAlertProps {
    botName: string;
    stepDescription?: string;
    isAdmin?: boolean;
    // Waiting funds details
    amount?: number;
    token?: string;
    chainId?: number;
    walletAddress?: string;
    // Binance deposit details
    binanceSubaccount?: string;
    binanceToken?: string;
    binanceAmount?: number;
    onCopy?: (text: string, label: string) => void;
}

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const hourglassRotate = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(180deg); }
  50% { transform: rotate(180deg); }
  75% { transform: rotate(360deg); }
`;

export const WaitingFundsAlert: React.FC<WaitingFundsAlertProps> = ({
    botName,
    stepDescription,
    isAdmin = false,
    amount,
    token,
    chainId,
    walletAddress,
    binanceSubaccount,
    binanceToken,
    binanceAmount,
    onCopy,
}) => {
    const theme = useTheme();

    if (!isAdmin) return null;

    const networkName = getNetworkName(chainId);
    
    // Determine if this is a Binance deposit (has binance details)
    const isBinanceDeposit = !!(binanceSubaccount || binanceToken);
    
    // Use Binance details if available, otherwise fall back to generic details
    const displayToken = isBinanceDeposit ? binanceToken : token;
    const displayAmount = isBinanceDeposit ? binanceAmount : amount;
    const displayAddress = isBinanceDeposit ? binanceSubaccount : walletAddress;
    
    const hasDepositDetails = displayAmount && displayToken;
    
    const handleCopyAddress = () => {
        if (!displayAddress) return;
        if (onCopy) {
            onCopy(displayAddress, isBinanceDeposit ? 'Binance Subaccount' : 'Address');
        } else {
            navigator.clipboard.writeText(displayAddress);
        }
    };

    // On-chain deposit addresses open in the network's explorer (Binance subaccounts have none).
    const explorerPrefix = !isBinanceDeposit && chainId ? NETWORKS[chainId]?.explorer : undefined;
    const handleOpenExplorer = () => {
        if (displayAddress && explorerPrefix) {
            window.open(`${explorerPrefix}${displayAddress}`, '_blank', 'noopener,noreferrer');
        }
    };

    // Waiting for external funds requires action from an operator: amber (warning).
    const accentColor = theme.palette.warning.main;

    // Where the funds are expected (a network, or the Binance subaccount): it is in the subtitle, so no
    // field repeats it.
    const destination = isBinanceDeposit ? 'Binance' : networkName;

    const fields: AlertField[] = [];
    if (hasDepositDetails) {
        fields.push({
            label: 'Amount to deposit',
            value: `${formatAmountWithCeiling(displayAmount)} ${displayToken}`,
        });
    } else if (stepDescription) {
        fields.push({ label: 'Current step', value: stepDescription });
    }

    return (
        <AlertShell
            color={accentColor}
            icon={
                <HourglassBottomRoundedIcon
                    sx={{ animation: `${hourglassRotate} 3s ease-in-out infinite` }}
                />
            }
            title={<AlertBotTitle botId={botName} />}
            // Same pattern as the gas alert: "<state> · <network>", sentence case, no inline icon.
            subtitle={destination ? `Awaiting funds · ${destination}` : 'Awaiting funds'}
            fields={fields}
            overlay={{
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(90deg, transparent 0%, ${alpha(accentColor, 0.05)} 50%, transparent 100%)`,
                backgroundSize: '200% 100%',
                animation: `${shimmer} 4s ease-in-out infinite`,
                pointerEvents: 'none',
            }}
            trailing={
                displayAddress ? (
                    <AddressPill
                        address={displayAddress}
                        onCopy={handleCopyAddress}
                        onOpenExplorer={explorerPrefix ? handleOpenExplorer : undefined}
                        copyLabel={isBinanceDeposit ? 'Copy subaccount' : 'Copy address'}
                    />
                ) : undefined
            }
        />
    );
};
